import React, { useState, useEffect, useCallback } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { apiGet, apiPatch, AuthError } from '../api';
import { useClient } from '../ClientContext';
import { token } from '../tokens';
import Icon from '../components/Icon';

/**
 * Overview.
 *
 * Four identically sized KPI boxes gave everything equal weight, which is the
 * generic admin-template shape at any width. The page now leads with one strip
 * that reads as a sentence — how many actions, how they split, how much spend
 * that protected — and the numbers that were tiles become small stats beneath
 * it.
 *
 * The width then buys a right-hand rail. Everything else on this page is a
 * figure; the queue is the only thing that is a task, so it is the only thing
 * that gets to be actionable.
 *
 * Spend Protected is the most compelling number in the product and was
 * previously just asserted. It is now the one clickable figure, and opens the
 * blocked actions that add up to it.
 */

const OUTCOME = {
  APPROVED: { cls: 'approved', label: 'Approved' },
  FLAG:     { cls: 'flagged',  label: 'Flagged' },
  BLOCK:    { cls: 'blocked',  label: 'Blocked' },
  OBSERVE:  { cls: 'observe',  label: 'Observe' },
};

const money = (n) => `£${(n || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const hours = (Date.now() - d.getTime()) / 36e5;
  if (hours < 1) return 'just now';
  if (hours < 24) return `${Math.round(hours)}h ago`;
  if (hours < 48) return 'yesterday';
  return `${Math.round(hours / 24)}d ago`;
};

function Skeleton() {
  return (
    <>
      <div className="kpi-grid" style={{ gridTemplateColumns: '296px minmax(0,1fr)' }}>
        <div className="skeleton skeleton-kpi" />
        <div className="skeleton skeleton-kpi" />
      </div>
      <div className="two-rail">
        <div className="skeleton" style={{ height: 462, borderRadius: 'var(--radius-md)' }} />
        <div className="skeleton" style={{ height: 462, borderRadius: 'var(--radius-md)' }} />
      </div>
      <div className="card" style={{ padding: 'var(--space-5)' }}>
        {[80, 60, 80, 40, 60].map((w, i) => (
          <div key={i} className={`skeleton skeleton-line w${w}`} />
        ))}
      </div>
    </>
  );
}

export default function Overview() {
  const { clientParam, selectedId, selected, isAll, observeMode } = useClient();

  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [trend, setTrend] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [drillOpen, setDrillOpen] = useState(false);
  const [blocked, setBlocked] = useState(null);   // null = not fetched yet
  const [deciding, setDeciding] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    setLoading(true);
    setDrillOpen(false);
    setBlocked(null);

    apiGet(`/stats${clientParam}`)
      .then((data) => {
        setStats(data.stats);
        setRecent(data.recent || []);
        setTrend(data.trend || []);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof AuthError ? err.message : err.message || 'Could not load the overview.');
        setLoading(false);
      });

    // The rail is useful but not essential — a failure here should not take
    // the page down with it.
    apiGet(`/approval-queue${clientParam}`)
      .then((data) => setQueue(data.items || []))
      .catch(() => setQueue([]));
  }, [clientParam]);

  const openDrill = useCallback(async () => {
    const next = !drillOpen;
    setDrillOpen(next);
    if (!next || blocked !== null) return;

    const params = new URLSearchParams({ outcome: 'BLOCK' });
    if (selectedId && selectedId !== 'ALL') params.set('client_id', selectedId);
    try {
      const data = await apiGet(`/decisions?${params.toString()}`);
      setBlocked(data.decisions || []);
    } catch {
      setBlocked([]);
    }
  }, [drillOpen, blocked, selectedId]);

  const decide = async (approvalId, status) => {
    setDeciding(approvalId);
    setNotice(null);
    try {
      const result = await apiPatch(`/approvals/${approvalId}`, { status });
      setQueue((q) => q.filter((i) => i.approval_id !== approvalId));
      setStats((s) => (s ? { ...s, pending: Math.max(0, (s.pending || 1) - 1) } : s));
      // The server is explicit that recording a decision does not execute the
      // campaign. Pass that through rather than saying "Approved", which would
      // read as "live".
      setNotice(result.message || 'Decision recorded.');
    } catch (err) {
      setNotice(err.message || 'Could not record that decision.');
    }
    setDeciding(null);
  };

  if (loading) {
    return (
      <>
        <div className="page-header">
          <h1 className="page-title">Overview</h1>
          <p className="page-subtitle">Loading the last 30 days…</p>
        </div>
        <Skeleton />
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="page-header">
          <h1 className="page-title">Overview</h1>
        </div>
        <div className="banner bad">
          <Icon name="alert" size={16} strokeWidth={2.2} />
          <span>{error}</span>
        </div>
      </>
    );
  }

  const total = stats.total_actions || 0;
  const pct = (n) => (total > 0 ? Math.round(((n || 0) / total) * 100) : 0);
  const blockRate = total > 0 ? ((stats.BLOCK / total) * 100).toFixed(1) : '0.0';

  const chart = {
    ok:   token('--ok', '#1A7A4A'),
    okBg: token('--ok-bg', '#E8F5EE'),
    warn: token('--warn', '#B7770D'),
    warnBg: token('--warn-bg', '#FDF6E3'),
    bad:  token('--bad', '#C0392B'),
    badBg: token('--bad-bg', '#FDECEA'),
    grid: token('--chart-grid', '#E5E7EB'),
    axis: token('--chart-axis', '#6B7280'),
  };

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Overview</h1>
        <p className="page-subtitle">
          {isAll
            ? 'Every AI buying action across all your clients.'
            : `${selected ? selected.name : ''} — every AI buying action evaluated against their governance rules.`}
        </p>
      </div>

      {total === 0 && (
        <div className="banner info">
          <Icon name="info" size={16} strokeWidth={2.2} />
          <span>
            <b>No actions recorded yet.</b> Figures appear here once an agent has proposed its
            first campaign through BidSense.
          </span>
        </div>
      )}

      {notice && (
        <div className="banner ok">
          <Icon name="check" size={16} strokeWidth={2.6} />
          <span>{notice}</span>
        </div>
      )}

      {/* ── Hero strip ── */}
      <div className="hero">
        <button className="hero-pine" onClick={openDrill} aria-expanded={drillOpen}>
          <div className="kpi-label">Spend protected</div>
          <div className="hero-value">{money(stats.total_protected)}</div>
          <span className="hero-cta">
            <Icon name="chevron" size={13} strokeWidth={2.6} />
            {drillOpen ? 'Hide' : 'See'} what this is made of
          </span>
        </button>

        <div className="hero-main">
          <div className="hero-head">
            <span className="card-title">Action outcomes</span>
            <span className="card-note">{total.toLocaleString('en-GB')} actions evaluated</span>
          </div>

          <div
            className="obar"
            role="img"
            aria-label={`${pct(stats.APPROVED)}% approved, ${pct(stats.FLAG)}% flagged, ${pct(stats.BLOCK)}% blocked`}
          >
            <span style={{ width: `${pct(stats.APPROVED)}%`, background: 'var(--ok)' }} />
            <span style={{ width: `${pct(stats.FLAG)}%`, background: 'var(--warn)' }} />
            <span style={{ width: `${pct(stats.BLOCK)}%`, background: 'var(--bad)' }} />
          </div>

          <div className="olegend">
            <div><i style={{ background: 'var(--ok)' }} />Approved <b>{(stats.APPROVED || 0).toLocaleString('en-GB')}</b><span className="pc">{pct(stats.APPROVED)}%</span></div>
            <div><i style={{ background: 'var(--warn)' }} />Flagged <b>{(stats.FLAG || 0).toLocaleString('en-GB')}</b><span className="pc">{pct(stats.FLAG)}%</span></div>
            <div><i style={{ background: 'var(--bad)' }} />Blocked <b>{(stats.BLOCK || 0).toLocaleString('en-GB')}</b><span className="pc">{pct(stats.BLOCK)}%</span></div>
          </div>

          <div className="hero-stats">
            <div><span className="hs-k">Block rate</span><span className="hs-v">{blockRate}%</span></div>
            <div>
              <span className="hs-k">Waiting on you</span>
              <span className={`hs-v ${stats.pending > 0 ? 'amber' : ''}`}>{stats.pending || 0}</span>
            </div>
            {/* Was hard-coded to "Observe" regardless of what the engine was
                doing. Blocked actions really are stopped, so saying otherwise
                here undercut the one claim the product makes. */}
            <div>
              <span className="hs-k">Mode</span>
              <span className="hs-v" style={{ color: observeMode ? 'var(--brass)' : 'var(--ok)' }}>
                {observeMode ? 'Observe' : 'Enforcing'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── What the protected figure is made of ── */}
      {drillOpen && (
        <div className="card drill-in" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="card-header">
            <span className="card-title">What {money(stats.total_protected)} is made of</span>
            <span className="card-note">Blocked before reaching Meta</span>
          </div>
          {blocked === null ? (
            <div style={{ padding: 'var(--space-5)' }}>
              <div className="skeleton skeleton-line w80" />
              <div className="skeleton skeleton-line w60" />
            </div>
          ) : blocked.length === 0 ? (
            <div className="empty">
              <h3>Nothing blocked yet</h3>
              <p>No action has been stopped by a rule, so there is nothing behind this figure.</p>
            </div>
          ) : (
            <div className="tbl-wrap">
              <table className="data-table dense">
                <thead>
                  <tr>
                    <th>Blocked campaign</th><th>Rule</th>
                    <th className="num">Daily budget</th><th className="num">Protected</th>
                  </tr>
                </thead>
                <tbody>
                  {blocked.slice(0, 12).map((b) => (
                    <tr key={b.action_id}>
                      <td style={{ fontWeight: 500 }}>{b.campaign_name}</td>
                      <td><span className="tag-mono">{b.rule_triggered || '—'}</span></td>
                      <td className="num">{money(b.spend_value)}</td>
                      <td className="num" style={{ fontWeight: 700, color: 'var(--ok)' }}>
                        {money(b.spend_protected)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Activity and the queue ── */}
      <div className="two-rail">
        <div className="card">
          <div className="card-header">
            <span className="card-title">Daily activity</span>
            <span className="legend">
              <span><i style={{ background: chart.ok }} />Approved</span>
              <span><i style={{ background: chart.warn }} />Flagged</span>
              <span><i style={{ background: chart.bad }} />Blocked</span>
            </span>
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height="100%" minHeight={300}>
              <AreaChart data={trend} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
                <CartesianGrid stroke={chart.grid} strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: chart.axis }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: chart.axis }} axisLine={false} tickLine={false} width={34} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: `1px solid ${chart.grid}` }} />
                <Area type="monotone" dataKey="approved" stackId="1" stroke={chart.ok}   fill={chart.okBg}   strokeWidth={2} />
                <Area type="monotone" dataKey="flagged"  stackId="1" stroke={chart.warn} fill={chart.warnBg} strokeWidth={2} />
                <Area type="monotone" dataKey="blocked"  stackId="1" stroke={chart.bad}  fill={chart.badBg}  strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">Waiting on you</span>
            <span className={`badge-count ${queue.length ? '' : 'none'}`}>{queue.length}</span>
          </div>
          <div className="q-list">
            {queue.length === 0 ? (
              <div className="empty" style={{ padding: '34px 20px' }}>
                <h3>Nothing waiting</h3>
                <p>Every flagged action has been dealt with.</p>
              </div>
            ) : (
              queue.map((item) => (
                <div className="q-item" key={item.approval_id}>
                  <div className="q-top">
                    <span className="q-name">
                      {item.campaign_name || item.proposed_action?.name || 'Unnamed campaign'}
                    </span>
                    <span className="q-age">{when(item.created_at)}</span>
                  </div>
                  <div className="q-why">
                    <Icon name="alert" size={13} strokeWidth={2.2} />
                    <span className="tag-mono">{item.rule_triggered}</span>
                  </div>
                  {item.reason && <div className="q-meta">{item.reason}</div>}
                  <div className="q-meta">
                    {isAll && item.client_name ? `${item.client_name} · ` : ''}
                    {money(item.spend_value)}/day
                  </div>
                  <div className="q-act">
                    <button
                      className="btn btn-approve"
                      disabled={deciding === item.approval_id}
                      onClick={() => decide(item.approval_id, 'approved')}
                    >
                      {deciding === item.approval_id ? '…' : 'Approve'}
                    </button>
                    <button
                      className="btn btn-reject"
                      disabled={deciding === item.approval_id}
                      onClick={() => decide(item.approval_id, 'rejected')}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── Recent actions ── */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Recent actions</span>
          <a href="/audit" className="btn btn-secondary" style={{ padding: '5px 11px', fontSize: 12, textDecoration: 'none' }}>
            View all <Icon name="right" size={13} strokeWidth={2.4} />
          </a>
        </div>
        {recent.length === 0 ? (
          <div className="empty">
            <h3>Nothing yet</h3>
            <p>Actions appear here as agents propose campaigns.</p>
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="data-table dense">
              <thead>
                <tr>
                  <th>Campaign</th>
                  {isAll && <th>Client</th>}
                  <th>Rule triggered</th>
                  <th>Outcome</th>
                  <th className="num">Daily budget</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((row) => {
                  const o = OUTCOME[row.outcome] || { cls: '', label: row.outcome };
                  return (
                    <tr key={row.action_id}>
                      <td style={{ fontWeight: 500 }}>{row.campaign_name}</td>
                      {isAll && <td style={{ color: 'var(--text-muted)' }}>{row.client_name}</td>}
                      <td>
                        {row.rule_triggered
                          ? <span className="tag-mono">{row.rule_triggered}</span>
                          : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                      <td><span className={`pill ${o.cls}`}>{o.label}</span></td>
                      <td className="num" style={{ fontWeight: 600 }}>{money(row.spend_value)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}